import ReorderIcon from "@mui/icons-material/Reorder";
import {Box} from "@mui/material";
import {ColumnDef} from "@tanstack/react-table";
import type {AggregationServiceCategoryListItem} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import {ServiceCategory} from "crqlar-react-common/dist/models/serviceCategory/serviceCategory.model";
import {CommonAggregationService} from "crqlar-react-common/dist/services/aggregation/commonAggregation.service";
import {ServiceCategoryService} from "crqlar-react-common/dist/services/serviceCategory/serviceCategory.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useHasGuestAppModule} from "crqlar-react-common/dist/utils/hooks/guestApp/useGuestAppModule";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    ServerTableToolbarAction,
    useServerTable
} from "../../common/serverTable";
import ReorderScreen from "../common/ReorderScreen";
import SettingsListPage from "../layout/SettingsListPage";

const ServiceCategoriesList = () => {
    useTrackScreenVisit('system service categories');

    const navigate = useNavigate();
    const {t} = useTranslation();
    const {localize} = useLocalize();
    const {hasGuestAppModule} = useHasGuestAppModule();
    const [reorderMode, setReorderMode] = useState(false);
    const [reorderItems, setReorderItems] = useState<ServiceCategory[]>([]);
    const [loadingReorder, setLoadingReorder] = useState(false);

    const enterReorder = async () => {
        setLoadingReorder(true);
        try {
            const response = await ServiceCategoryService.getAllServiceCategories({
                limit: {offset: 0, limit: 1000, sort: [{field: 'orderPriority', direction: 'ASC'}]}
            });
            setReorderItems(response.content || []);
            setReorderMode(true);
        } finally {
            setLoadingReorder(false);
        }
    };

    const columns = useMemo<ColumnDef<AggregationServiceCategoryListItem>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: category => category.name,
            cell: ({row}) => <Box>{row.original.name ?? ''}</Box>,
            size: 280,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'sectors',
            header: t('Sectors'),
            accessorFn: category => category.sectors.map(sector => sector.name).filter(Boolean),
            cell: ({row}) => (
                <Box>{row.original.sectors.map(sector => sector.name).filter(Boolean).join(', ')}</Box>
            ),
            size: 320,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: 'sectorName'
            }
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: category => category.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: category => category.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable({
        tableId: 'system-service-categories',
        columns,
        getRowId: category => category.id,
        onRowClick: category => navigate(`/system/serviceCategories/${category.id}`),
        initialSorting: [{id: 'sectors', desc: false}],
        queryFn: ({pagination, searchQuery, sorting}) => CommonAggregationService.listServiceCategories({
            name: searchQuery ? [searchQuery] : undefined,
            limit: {
                offset: pagination.pageIndex * pagination.pageSize,
                limit: pagination.pageSize,
                sort: sorting ? [sorting] : undefined
            }
        })
    });

    if (reorderMode) {
        return (
            <ReorderScreen<ServiceCategory>
                title={t('Reorder Service Categories')}
                hint={t('Drag categories to set the display order in the guest app.')}
                getLabel={category => localize(category.nameLocalized) || category.name || '-'}
                initialItems={reorderItems}
                save={category => ServiceCategoryService.updateServiceCategory(category)}
                onClose={() => {
                    setReorderMode(false);
                    void controller.query.refetch();
                }}
            />
        );
    }

    return (
        <SettingsListPage title={t('Service categories')} back={() => navigate('/system')}>
                <ServerTableProvider controller={controller}>
                    <ServerTableFrame>
                        <ServerTableToolbar hoistOnCompact>
                            {hasGuestAppModule && (
                                <ServerTableToolbarAction
                                    align="start"
                                    variant="text"
                                    startIcon={<ReorderIcon/>}
                                    onClick={enterReorder}
                                    disabled={loadingReorder}
                                >
                                    {t('Reorder')}
                                </ServerTableToolbarAction>
                            )}
                            <ServerTableSearch/>
                            <ServerTableSort/>
                            <ServerTableFullscreen/>
                            <ServerTableColumns/>
                            <ServerTableCreate
                                label={t('Create Service Category')}
                                onClick={() => navigate('/system/serviceCategories/new')}
                            />
                        </ServerTableToolbar>

                        <ServerTable/>
                    </ServerTableFrame>
                </ServerTableProvider>
        </SettingsListPage>
    );
};

export default ServiceCategoriesList;
